// The theme in force, as the Shell marks it on <html> (data-theme-kind, data-palette,
// data-studio-theme, data-close-glyph) from the resolved look (lib/themeLayers.ts).
import type { CloseGlyphKind, LogoKind, ScrollIndicatorKind, ThemeSpec } from '@/kinds/themeprofile/schema'
import { canDraw } from '@/lib/themeStudio'
import type { ResolvedLook } from '@/lib/themeLayers'

/** The glyph a built-in palette gives the close button; the others keep the X. */
const PALETTE_GLYPH: Record<string, CloseGlyphKind> = { halloween: 'pumpkin' }
/** The nav logo and the scroll indicator a built-in palette gives; the others keep the mark and none. */
const PALETTE_LOGO: Record<string, LogoKind> = { halloween: 'pumpkin' }
const PALETTE_SCROLL: Record<string, ScrollIndicatorKind> = { halloween: 'witch' }

/**
 * The close glyph the look in force wears: a palette's own, or the `icons.close` slot of a studio
 * theme that is drawn (unset, it takes its base palette's glyph, so a theme saved from Halloween
 * deluxe keeps the pumpkin; `x` opts out) (not on the palette brightness, which the Shell never draws a studio theme
 * on). The Shell writes it on <html> as `data-close-glyph`.
 */
export const closeGlyphOf = (r: ResolvedLook): CloseGlyphKind => {
  const t = r.theme
  if (t.kind === 'palette') return PALETTE_GLYPH[t.theme] ?? 'x'
  if (t.kind === 'studio' && !r.fixed && canDraw(t.spec))
    return t.spec.icons?.close ?? (t.spec.base ? PALETTE_GLYPH[t.spec.base] : undefined) ?? 'x'
  return 'x'
}

/** Which palette's slot a look draws from: its own, or a drawn studio theme's `base`. */
const slotOf = <K extends string>(
  r: ResolvedLook,
  table: Record<string, K>,
  own: (icons: NonNullable<ThemeSpec['icons']>) => K | undefined,
  none: K,
): K => {
  const t = r.theme
  if (t.kind === 'palette') return table[t.theme] ?? none
  if (t.kind === 'studio' && !r.fixed && canDraw(t.spec))
    return (t.spec.icons && own(t.spec.icons)) ?? (t.spec.base ? table[t.spec.base] : undefined) ?? none
  return none
}

/** The nav logo the look in force wears (`icons.logo`, else its base palette's, else the mark). */
export const logoOf = (r: ResolvedLook): LogoKind => slotOf(r, PALETTE_LOGO, (i) => i.logo, 'mark')

/** The scroll indicator the look in force wears (`icons.scroll`, else its base palette's, else none). */
export const scrollIndicatorOf = (r: ResolvedLook): ScrollIndicatorKind =>
  slotOf(r, PALETTE_SCROLL, (i) => i.scroll, 'none')

/** The theme in force as plain attributes for <html>: its kind and its palette or studio theme name. */
export const themeMarks = (r: ResolvedLook): { kind: string; palette?: string; studio?: string } => {
  const t = r.theme
  if (t.kind === 'palette') return { kind: 'palette', palette: t.theme }
  if (t.kind === 'studio') return { kind: 'studio', studio: t.profile }
  return { kind: 'horizon' }
}
